<template>
  <v-app>
    <!-- Modern Header -->
    <PublicHeader />

    <!-- Loading Overlay -->
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Main Content -->
    <v-main :style="$vuetify.breakpoint.xs ? 'padding:0px!important;margin-top: 100px;' : 'padding:0px!important;margin-top: 80px;'">
      <div v-if="vehicle" class="vehicle-detail-container">

        <!-- Breadcrumb Section -->
        <section class="breadcrumb-section">
          <v-container class="py-3">
            <v-breadcrumbs :items="breadcrumbItems" class="pa-0">
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :href="item.href"
                  :disabled="item.disabled"
                  class="breadcrumb-item"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
              <template v-slot:divider>
                <v-icon class="material-icons-round">chevron_right</v-icon>
              </template>
            </v-breadcrumbs>
          </v-container>
        </section>

        <!-- Main Vehicle Content -->
        <section class="vehicle-content-section">
          <v-container>
            <v-row>
              <!-- Left Column - Images and Details -->
              <v-col cols="12" lg="8">

                <!-- Vehicle Title -->
                <div class="vehicle-title-section mb-4">
                  <h1 class="vehicle-title">{{ getVehicleTitle() }}</h1>
                  <div class="vehicle-subtitle">
                    <span v-if="vehicle.odometer" class="subtitle-item">{{ formatMileage(vehicle.odometer) }}</span>
                    <span v-if="vehicle.exterior_color" class="subtitle-item">{{ vehicle.exterior_color }}</span>
                    <span v-if="vehicle.vin" class="subtitle-item">VIN: {{ vehicle.vin.slice(-6) }}</span>
                  </div>
                </div>

                <!-- Image Gallery -->
                <div class="image-gallery-section mb-6">
                  <v-card class="image-gallery-card" flat>
                    <v-carousel
                      v-if="vehicle.images && vehicle.images.length > 0"
                      height="450"
                      show-arrows-on-hover
                      hide-delimiters
                      class="vehicle-carousel"
                    >
                      <v-carousel-item
                        v-for="(image, i) in vehicle.images"
                        :key="i"
                      >
                        <v-img
                          :src="getImageSrc(image.image)"
                          height="450"
                          cover
                          @error="handleImageError"
                          class="vehicle-image"
                        >
                          <template v-slot:placeholder>
                            <v-img
                              :src="fallbackImage"
                              height="450"
                              cover
                              class="vehicle-image"
                            ></v-img>
                          </template>
                        </v-img>
                      </v-carousel-item>
                    </v-carousel>

                    <!-- Fallback single image -->
                    <v-img
                      v-else
                      :src="fallbackImage"
                      height="450"
                      cover
                      class="vehicle-image"
                    ></v-img>
                  </v-card>
                </div>

                <!-- Vehicle Overview -->
                <div class="vehicle-overview-section mb-6">
                  <v-card class="overview-card" flat>
                    <v-card-title class="overview-title">
                      <h2>Vehicle Overview</h2>
                    </v-card-title>
                    <v-card-text class="overview-content">
                      <v-row>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.year">
                          <div class="overview-item">
                            <div class="overview-label">Year</div>
                            <div class="overview-value">{{ vehicle.year }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.make">
                          <div class="overview-item">
                            <div class="overview-label">Make</div>
                            <div class="overview-value">{{ vehicle.make }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.model">
                          <div class="overview-item">
                            <div class="overview-label">Model</div>
                            <div class="overview-value">{{ vehicle.model }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.trim">
                          <div class="overview-item">
                            <div class="overview-label">Trim</div>
                            <div class="overview-value">{{ vehicle.trim }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.body_style">
                          <div class="overview-item">
                            <div class="overview-label">Body Style</div>
                            <div class="overview-value">{{ vehicle.body_style }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.engine">
                          <div class="overview-item">
                            <div class="overview-label">Engine</div>
                            <div class="overview-value">{{ vehicle.engine }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.transmission">
                          <div class="overview-item">
                            <div class="overview-label">Transmission</div>
                            <div class="overview-value">{{ vehicle.transmission }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.fuel_type">
                          <div class="overview-item">
                            <div class="overview-label">Fuel Type</div>
                            <div class="overview-value">{{ vehicle.fuel_type }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.drivetrain">
                          <div class="overview-item">
                            <div class="overview-label">Drivetrain</div>
                            <div class="overview-value">{{ vehicle.drivetrain }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.exterior_color">
                          <div class="overview-item">
                            <div class="overview-label">Exterior Color</div>
                            <div class="overview-value">{{ vehicle.exterior_color }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.interior_color">
                          <div class="overview-item">
                            <div class="overview-label">Interior Color</div>
                            <div class="overview-value">{{ vehicle.interior_color }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.doors">
                          <div class="overview-item">
                            <div class="overview-label">Doors</div>
                            <div class="overview-value">{{ vehicle.doors }}</div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Right Column - Pricing & Contact -->
              <v-col cols="12" lg="4">

                <!-- Price Card -->
                <v-card class="price-card sticky-card" elevation="2">
                  <v-card-text class="price-content">
                    <div class="price-section">
                      <div class="price-main">{{ formatPrice(vehicle.amount) }}</div>
                      <div class="price-disclaimer">*Price excludes taxes, title, and fees</div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Key Vehicle Stats -->
                    <div class="key-stats mb-4">
                      <div class="stat-row" v-if="vehicle.odometer">
                        <span class="stat-label">Mileage</span>
                        <span class="stat-value">{{ formatMileage(vehicle.odometer) }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.exterior_color">
                        <span class="stat-label">Exterior</span>
                        <span class="stat-value">{{ vehicle.exterior_color }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.interior_color">
                        <span class="stat-label">Interior</span>
                        <span class="stat-value">{{ vehicle.interior_color }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.fuel_type">
                        <span class="stat-label">Fuel Type</span>
                        <span class="stat-value">{{ vehicle.fuel_type }}</span>
                      </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                      <v-btn
                        color="green"
                        block
                        large
                        dark
                        class="mb-3 action-btn-primary"
                        @click="openContactDealerModal"
                      >
                        <v-icon left class="material-icons-round">phone</v-icon>
                        Contact Dealer
                      </v-btn>

                      <!-- <v-btn
                        color="green"
                        block
                        large
                        outlined
                        class="mb-3 action-btn-secondary"
                        @click="scheduleTestDrive"
                      >
                        Schedule Test Drive
                      </v-btn> -->

                      <v-btn
                        color="grey darken-1"
                        block
                        large
                        outlined
                        class="action-btn-secondary"
                        @click="saveVehicle"
                      >
                        Save Vehicle
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Vehicle Details Card -->
                <v-card class="details-card mt-4" elevation="1">
                  <v-card-title class="details-title">
                    <h3>Vehicle Details</h3>
                  </v-card-title>
                  <v-card-text class="details-content">
                    <div class="detail-list">
                      <div class="detail-row" v-if="vehicle.vin">
                        <span class="detail-label">VIN</span>
                        <span class="detail-value">{{ vehicle.vin }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.stock_number">
                        <span class="detail-label">Stock #</span>
                        <span class="detail-value">{{ vehicle.stock_number }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.transmission">
                        <span class="detail-label">Transmission</span>
                        <span class="detail-value">{{ vehicle.transmission }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.drivetrain">
                        <span class="detail-label">Drivetrain</span>
                        <span class="detail-value">{{ vehicle.drivetrain }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.engine">
                        <span class="detail-label">Engine</span>
                        <span class="detail-value">{{ vehicle.engine }}</span>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </section>

      </div>

      <!-- Error State -->
      <div v-else-if="!loading" class="error-state">
        <v-container>
          <div class="text-center py-12">
            <v-icon size="120" color="grey lighten-2">mdi-car-off</v-icon>
            <h2 class="mt-4 mb-2">Vehicle Not Found</h2>
            <p class="mb-4">The vehicle you're looking for could not be found.</p>
            <v-btn color="primary" @click="$router.push('/inventory')">
              <v-icon left class="material-icons-round">arrow_left</v-icon>
              Back to Inventory
            </v-btn>
          </div>
        </v-container>
      </div>
    </v-main>

    <!-- Modern Footer -->
    <PublicFooter />

    <!-- Contact Dealer Modal -->
    <ContactDealerModal
      v-model="showContactDealerModal"
      :vehicle="vehicle"
    />
  </v-app>
</template>

<script>
import PublicHeader from '@/components/PublicHeader.vue'
import PublicFooter from '@/components/PublicFooter.vue'
import ContactDealerModal from '@/components/ContactDealerModal.vue'

export default {
  name: 'VehicleDetailPage',
  components: {
    PublicHeader,
    PublicFooter,
    ContactDealerModal
  },
  data() {
    return {
      vehicle: null,
      loading: true,
      fallbackImage: require('@/assets/img/coming-soon.jpg'),
      brokenImages: new Set(),
      showContactDealerModal: false
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        {
          text: 'Home',
          disabled: false,
          href: '/'
        },
        {
          text: 'Inventory',
          disabled: false,
          href: '/inventory'
        },
        {
          text: this.vehicle ? this.getVehicleTitle() : 'Vehicle',
          disabled: true,
          href: '#'
        }
      ]
    }
  },
  mounted() {
    this.loadVehicle()
  },
  methods: {
    async loadVehicle() {
      try {
        const vehicleId = this.$route.params.id
        const domain = window.location.host
        
        const response = await axios.post(`/api/get-vehicle/${vehicleId}`, {
          domain: domain
        })
        
        if (response.data && response.data.data) {
          this.vehicle = response.data.data
        } else {
          console.error('Vehicle not found')
        }
      } catch (error) {
        console.error('Error loading vehicle:', error)
      } finally {
        this.loading = false
      }
    },

    getImageSrc(imageSrc) {
      if (!imageSrc || this.brokenImages.has(imageSrc)) {
        return this.fallbackImage
      }
      
      if (imageSrc.startsWith('http') || imageSrc.startsWith('data:image')) {
        return imageSrc
      }
      
      return this.fallbackImage
    },

    handleImageError(event) {
      const originalSrc = event.target.src
      if (originalSrc && !originalSrc.includes('coming-soon.jpg')) {
        this.brokenImages.add(originalSrc)
      }
      event.target.src = this.fallbackImage
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'
      
      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)
      
      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return '$0'
      
      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return '$0'
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    formatMileage(mileage) {
      if (!mileage) return 'N/A'

      const numMileage = parseInt(mileage)
      if (isNaN(numMileage)) return 'N/A'

      return new Intl.NumberFormat('en-US').format(numMileage) + ' mi'
    },

    contactDealer() {
      // Fallback phone call
      const phoneNumber = '6026101072'
      window.location.href = `tel:${phoneNumber}`
    },

    openContactDealerModal() {
      this.showContactDealerModal = true
    },

    scheduleTestDrive() {
      // TODO: Implement schedule test drive functionality
      console.log('Schedule test drive clicked')
    },

    saveVehicle() {
      // TODO: Implement save vehicle functionality
      console.log('Save vehicle clicked')
    }
  }
}
</script>

<style scoped>
/* Main Layout */
.vehicle-detail-container {
  background: #ffffff;
  min-height: 100vh;
}

/* Breadcrumb Section */
.breadcrumb-section {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.breadcrumb-item {
  font-size: 0.875rem;
  color: #6c757d;
}

.breadcrumb-item:not(.v-breadcrumbs-item--disabled) {
  color: #007bff;
}

/* Vehicle Content Section */
.vehicle-content-section {
  padding: 2rem 0;
  background: #ffffff;
  min-height: 100vh;
}

/* Vehicle Title Section */
.vehicle-title-section {
  margin-bottom: 2rem;
}

.vehicle-title {
  font-size: 2rem !important;
  font-weight: 700 !important;
  color: #212529 !important;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.vehicle-subtitle {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  color: #6c757d;
  font-size: 0.875rem;
}

.subtitle-item {
  position: relative;
}

.subtitle-item:not(:last-child)::after {
  content: "•";
  margin-left: 1rem;
  color: #dee2e6;
}

/* Image Gallery Section */
.image-gallery-section {
  margin-bottom: 2rem;
}

.image-gallery-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

.vehicle-carousel {
  border-radius: 12px;
}

.vehicle-image {
  object-fit: cover;
}

/* Vehicle Overview Section */
.vehicle-overview-section {
  margin-bottom: 2rem;
}

.overview-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
}

.overview-title {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem !important;
}

.overview-title h2 {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
  margin: 0;
}

.overview-content {
  padding: 1.5rem !important;
}

.overview-item {
  text-align: left;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: 100%;
}

.overview-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.overview-value {
  font-size: 0.875rem;
  color: #212529;
  font-weight: 600;
}

/* Price Card */
.price-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  margin-bottom: 2rem;
}

.sticky-card {
  position: sticky;
  top: 140px;
  z-index: 10;
}

.price-content {
  padding: 2rem !important;
}

.price-section {
  text-align: center;
  margin-bottom: 1rem;
}

.price-main {
  font-size: 2.25rem;
  font-weight: 800;
  color: #28a745;
  margin-bottom: 0.5rem;
}

.price-disclaimer {
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
}

/* Key Stats */
.key-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.stat-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.875rem;
}

.stat-value {
  font-weight: 600;
  color: #212529;
  font-size: 0.875rem;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-btn-primary {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-size: 1rem !important;
}

.action-btn-secondary {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-size: 1rem !important;
}

/* Details Card */
.details-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
  margin-top: 1.5rem;
}

.details-title {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem !important;
}

.details-title h3 {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
  margin: 0;
}

.details-content {
  padding: 1.5rem !important;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.detail-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 600;
  color: #212529;
  font-size: 0.875rem;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

/* Error State */
.error-state {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .sticky-card {
    position: static;
    top: auto;
    z-index: auto;
  }
}

@media (max-width: 960px) {
  .vehicle-content-section {
    padding: 1.5rem 0;
  }

  .vehicle-title {
    font-size: 1.75rem !important;
  }

  .vehicle-subtitle {
    flex-direction: column;
    gap: 0.5rem;
  }

  .subtitle-item:not(:last-child)::after {
    display: none;
  }

  .image-gallery-card {
    border-radius: 8px !important;
  }

  .overview-card {
    border-radius: 8px !important;
  }

  .overview-title {
    padding: 1rem !important;
  }

  .overview-content {
    padding: 1rem !important;
  }

  .price-card {
    border-radius: 8px !important;
    margin-bottom: 1.5rem;
  }

  .price-content {
    padding: 1.5rem !important;
  }

  .price-main {
    font-size: 2rem;
  }

  .details-card {
    border-radius: 8px !important;
  }

  .details-title {
    padding: 1rem !important;
  }

  .details-content {
    padding: 1rem !important;
  }
}

@media (max-width: 600px) {
  .vehicle-content-section {
    padding: 1rem 0;
  }

  .vehicle-title {
    font-size: 1.5rem !important;
  }

  .vehicle-subtitle {
    font-size: 0.8rem;
  }

  .image-gallery-card {
    border-radius: 6px !important;
  }

  .overview-card {
    border-radius: 6px !important;
  }

  .overview-title {
    padding: 0.75rem !important;
  }

  .overview-content {
    padding: 0.75rem !important;
  }

  .overview-item {
    padding: 0.75rem;
  }

  .price-card {
    border-radius: 6px !important;
  }

  .price-content {
    padding: 1rem !important;
  }

  .price-main {
    font-size: 1.75rem;
  }

  .action-btn-primary,
  .action-btn-secondary {
    padding: 10px 20px !important;
    font-size: 0.9rem !important;
  }

  .details-card {
    border-radius: 6px !important;
  }

  .details-title {
    padding: 0.75rem !important;
  }

  .details-content {
    padding: 0.75rem !important;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .detail-value {
    text-align: left;
    max-width: 100%;
  }

  .breadcrumb-section {
    padding: 0.5rem 0;
  }
}
</style>
